import React, { Component, createRef } from "react";
import Dropzone from "react-dropzone";
import {
  get as _get, merge as _merge, isEmpty as _isEmpty, cloneDeep as _cloneDeep, set as _set,
  isEqual as _isEqual, filter as _filter, reject as _reject, sortBy as _sortBy, findIndex as _findIndex
} from "lodash";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { FormattedMessage, injectIntl } from "react-intl";
import { Dropdown } from "react-bootstrap";
import { connect } from "react-redux";

import { Select, ErrorMessage, showAlertMessage } from "../../../../resusableComponents/common/formElements";
import { withRouter } from "../../../../resusableComponents/hoc/withRouter";

import { updatePageLevelLoadingState } from "../../../../redux/actions/application";

import { doGetClientList } from "../../../../redux/services/userMangement";
import { getMasterColumnLists, getClientTemplateDetailsById, createOrUpdateTemplate, uploadClientTemplateHeaders, getCompositeTemplateFieldLookup } from "../../../../redux/services/clientTemplates";

// const concatenateTypeList = [
//   { id: 0, name: <FormattedMessage id="client_template.add.concat" defaultMessage="CONCAT" /> },
//   { id: 1, name: <FormattedMessage id="client_template.add.coalesce" defaultMessage="COALESCE" /> }
// ];

const templateDropzoneRef = createRef();

class AddOrUpdateTemplate extends Component {

  constructor(props) {
    super(props);

    // Mode IDs (from backend): 1=Source(Ignore), 2=System, 3=Constant, 4=Composed
    this.MODE_SOURCE = 1;
    this.MODE_SYSTEM = 2;
    this.MODE_CONSTANT = 4;
    this.MODE_COMPOSED = 5;

    // System row — single-select Hermes field
    this.defaultSystemColumn = {
      columnName: null,
      friendlyName: "",
      isSpecial: false,
      mode: 2,
      constantValue: "",
      composedConfig: []
    };

    // Constant row — user enters a fixed literal value
    this.defaultConstantColumn = {
      columnName: null,
      friendlyName: "",
      isSpecial: false,
      mode: 4,
      constantValue: "",
      composedConfig: []
    };

    // Composed row — multiple Hermes fields joined by separators
    this.defaultComposedColumn = {
      columnName: null,
      friendlyName: "",
      isSpecial: false,
      mode: 5,
      constantValue: "",
      composedConfig: {
        segments: [{ composedValue: null, key: "", pairSeparator: "", keyValueSeparator: "" }]
      }
    };

    // Keep alias for legacy code paths
    this.defaultSimpleColumn = this.defaultSystemColumn;

    this.defaultSpecialColumn = {
      concatType: "",
      friendlyName: "",
      columnFields: [{
        concatText: ",",
        columnName: []
      }],
      isSpecial: true
    };

    this.defaultInnerColumnFields = {
      concatText: "",
      columnName: []
    };

    this.defaultNoValue = {
      name: "",
      selectedAgency: [],
      selectedClient: [],
      simpleColumns: [],
      specialColumns: [],
      indexNo: 0,
      indexValue: 0,
      editTemplateData: [],
    };

    this.state = {
      ...this.defaultNoValue,
      masterColumnsList: [],
      compositeColumnsList: [],
      activeRequestCount: 0,
      formError: {},
      uploadedFileName: ""
    };

    this._skipNextClientReset = false;
  };

  componentDidMount() {
    this._fetchMasterColumnLists();
    this._fetchCompositeColumnLists();
  };

  componentDidUpdate(prevProps, prevState) {
    if (!_isEqual(prevState.selectedAgency, this.state.selectedAgency) && (!_get(this.props, "editTemplateId", null) || _get(this.props, "isCloneMode", false))) {
      this._fetchClientList();
    }
  };

  _incrementLoader = () => {
    const { updatePageLevelLoadingState } = this.props;
    this.setState((prevState) => {
      const nextCount = (_get(prevState, "activeRequestCount", 0) + 1);
      if (nextCount === 1 && typeof updatePageLevelLoadingState === "function") { updatePageLevelLoadingState(true); }
      return { activeRequestCount: nextCount };
    });
  }

  _decrementLoader = () => {
    const { updatePageLevelLoadingState } = this.props;
    this.setState((prevState) => {
      const nextCount = Math.max(0, _get(prevState, "activeRequestCount", 0) - 1);
      if (nextCount === 0 && typeof updatePageLevelLoadingState === "function") { updatePageLevelLoadingState(false); }
      return { activeRequestCount: nextCount };
    });
  }

  _fetchMasterColumnLists = async () => {
    const { editTemplateId } = this.props;

    try {
      this._incrementLoader();

      const response = await getMasterColumnLists();

      if ((response.flag || false) === true) {
        const masterColList = _get(response, "data.responseData", []);

        this.defaultSpecialColumn = {
          ...this.defaultSpecialColumn,
          concatType: { id: 0, name: <FormattedMessage id="client_template.manage_template.concat" defaultMessage="CONCAT" /> },
          columnFields: [{
            concatText: ",",
            columnName: _get(masterColList, "[0]", null)
          }]
        };

        this.defaultInnerColumnFields = {
          ...this.defaultInnerColumnFields,
          concatText: ",",
          columnName: _get(masterColList, "[0]", null)
        }

        this.setState({ masterColumnsList: masterColList }, () => {
          if (editTemplateId) {
            this._fetchTemplateDetailsByID();
          }
        });
      } else {
        showAlertMessage((_get(response, "message", "Something went wrong while fetching master field list.")));
      }
    } catch (error) {
      showAlertMessage(_get(error, "message", "Something went wrong while fetching master field list."));

    } finally {
      this._decrementLoader();
    }
  }

  _fetchCompositeColumnLists = async () => {
    const { editTemplateId } = this.props;
    try {
      this._incrementLoader();
      const response = await getCompositeTemplateFieldLookup(editTemplateId);
      if ((response.flag || false) === true) {
        const rawData = _get(response, "data.responseData", []);
        // Map unique id for dropdown
        const compositeColList = rawData.map(col => ({
          ...col,
          id: col.source_kind === 'column' ? `col_${col.sequence_number}` : `fld_${col.client_template_field_lookup_id}`,
          friendly_name: _get(col, "friendly_name", "")
        }));
        this.setState({ compositeColumnsList: compositeColList });
      } else {
        showAlertMessage((_get(response, "message", "Something went wrong while fetching composite field list.")));
      }
    } catch (error) {
      showAlertMessage(_get(error, "message", "Something went wrong while fetching composite field list."));
    } finally {
      this._decrementLoader();
    }
  }

  _fetchClientList = async () => {
    const { selectedAgency } = this.state;

    try {
      this._incrementLoader();

      const response = await doGetClientList({ agency_id: _get(selectedAgency, "id", null), dropdown_flag: 1 });

      if ((response.flag || false) === true) {
        if (this._skipNextClientReset) {
          this._skipNextClientReset = false;
          this.setState({ filterClientList: _get(response, "data.responseData.client_list", []) });
        } else {
          this.setState({ filterClientList: _get(response, "data.responseData.client_list", []), selectedClient: [] });
        }
      } else {
        showAlertMessage((_get(response, "message", "Something went wrong while fetching client list.")));
        this.setState({ filterClientList: [], selectedClient: [] });
      }

    } catch (error) {
      showAlertMessage(_get(error, "message", "Something went wrong while fetching client list."));

    } finally {
      this._decrementLoader();
    }
  }

  _fetchTemplateDetailsByID = async () => {
    const { editTemplateId } = this.props;
    const { masterColumnsList } = this.state;

    try {
      this._incrementLoader();

      const response = await getClientTemplateDetailsById(editTemplateId);

      if ((response.flag || false) === true) {

        const editTemplateData = _get(response, "data.responseData", []);

        const tmpSimpleColumns = [];
        const tmpSpecialColumns = [];

        let tmpSelectedAgency = { id: _get(editTemplateData, "client_template[0].agency_id", null), agency_name: _get(editTemplateData, "client_template[0].agency_name", "") };
        let tmpSelectedClient = { id: _get(editTemplateData, "client_template[0].client_id", null), client_name: _get(editTemplateData, "client_template[0].client_name", "") };

        Object.values(editTemplateData.client_template_detail || []).forEach((e, i) => {
          const rowMode = _get(e, "mode", 2); // integer: 2=System, 3=Constant, 4=Composed
          const friendlyName = _get(e, "friendly_name", "");
          const displayOrder = _get(e, "sequence_number", i);

          if (rowMode === 5) {
            // Composed — read composed_config object; separators are per segment
            const rawConfig = _get(e, "composed_config", {}) || {};
            const rawSegments = _get(rawConfig, "segments", []) || [];
            const segments = (rawSegments || []).map(seg => {
              const fieldId = _get(seg, "source.client_template_field_lookup_id", null);
              const seqNo = _get(seg, "source.sequence_number", null);
              const composedValue = (fieldId !== null) ? `fld_${fieldId}` : (seqNo !== null ? `col_${seqNo}` : null);

              return {
                composedValue: composedValue,
                key: _get(seg, "key", ""),
                pairSeparator: _get(seg, "pair_separator", ""),
                keyValueSeparator: _get(seg, "key_value_separator", "")
              };
            });

            tmpSimpleColumns.push({
              columnName: null,
              friendlyName,
              displayOrder,
              isSpecial: false,
              mode: 5,
              constantValue: "",
              composedConfig: {
                segments: segments.length > 0 ? segments : [{ composedValue: null, key: "", pairSeparator: "", keyValueSeparator: "" }]
              }
            });
          } else if (rowMode === 4) {
            // Constant — read constant_value
            tmpSimpleColumns.push({
              columnName: null,
              friendlyName,
              displayOrder,
              isSpecial: false,
              mode: 4,
              constantValue: _get(e, "constant_value", ""),
              composedConfig: []
            });
          } else {
            // System (mode 2) or any unknown — single lookup id
            let matchedColumn = (masterColumnsList || []).find(
              col => col.id === _get(e, "client_template_field_lookup_id", null)
            ) || null;

            if (!matchedColumn) {
              matchedColumn = (masterColumnsList || []).find(col => col.id === -1) || null;
            }

            tmpSimpleColumns.push({
              columnName: matchedColumn,
              friendlyName,
              displayOrder,
              isSpecial: false,
              mode: 2,
              constantValue: "",
              composedConfig: []
            });
          }
        });

        if (_get(this.props, "isCloneMode", false)) {
          this._skipNextClientReset = true;
        }

        this.setState({
          name: (_get(this.props, "isCloneMode", false) ? `Clone - ${_get(editTemplateData, "client_template[0].template_name", "")}` : _get(editTemplateData, "client_template[0].template_name", "")),
          selectedAgency: (tmpSelectedAgency || []),
          selectedClient: (tmpSelectedClient || []),
          simpleColumns: (tmpSimpleColumns || []),
          specialColumns: (tmpSpecialColumns || []),
          editTemplateData: (editTemplateData || []),
          indexNo: _get(editTemplateData, "client_template_detail", []).length,
          editTemplateId: (editTemplateId || null)
        });
      } else {
        showAlertMessage((_get(response, "message", "Something went wrong while fetching template details.")));
      }
    } catch (error) {
      showAlertMessage(_get(error, "message", "Something went wrong while fetching template details."));

    } finally {
      this._decrementLoader();
    }
  }

  _onDropTemplateFile = async (acceptedFiles) => {
    const { intl } = this.props;
    const { masterColumnsList } = this.state;

    if (!acceptedFiles || acceptedFiles.length === 0) { return; }

    const firstFile = acceptedFiles[0];
    const formData = new FormData();
    formData.append("formFile", firstFile);

    try {
      this._incrementLoader();

      const response = await uploadClientTemplateHeaders(formData);

      if (_get(response, "flag", false) === true) {
        const responseData = _get(response, "data", []);

        // Map API response into simpleColumns — matched rows default to mode 2 (System).
        // If hermes field does not match / is Ignore, set to mode 1 (Source/Ignore).
        const ignoreOption = (masterColumnsList || []).find(col => col.id === -1) || null;

        const newSimpleColumns = (responseData || []).map((item, i) => {
          const matchedValue = _get(item, "matched_list_Value.value", "");
          const matchedKey = _get(item, "matched_list_Value.key", "0");
          const headerColumn = _get(item, "headerColumn", "");

          let matchedColumn;
          let rowMode;
          if (String(matchedKey) !== "0" && matchedValue && matchedValue.toLowerCase() !== "ignore") {
            // Try to find a matching column; fall back to Ignore if not found
            const found = (masterColumnsList || []).find(
              (col) => (col.friendly_name || "").toLowerCase().trim() === (matchedValue || "").toLowerCase().trim()
            );
            if (found && found.id !== -1) {
              matchedColumn = found;
              rowMode = 2; // System
            } else {
              matchedColumn = ignoreOption;
              rowMode = 1; // Source/Ignore
            }
          } else {
            // Key 0 or explicitly "ignore" → set to Ignore option with mode 1
            matchedColumn = ignoreOption;
            rowMode = 1; // Source/Ignore
          }

          return {
            ...this.defaultSystemColumn,
            columnName: matchedColumn,
            friendlyName: headerColumn,
            displayOrder: i,
            isSpecial: false,
            mode: rowMode
          };
        });

        this.setState({
          uploadedFileName: firstFile.name,
          simpleColumns: newSimpleColumns,
          indexNo: newSimpleColumns.length
        });

        showAlertMessage(
          intl.formatMessage({ id: "client_template.manage_template.upload_headers_success", defaultMessage: "File uploaded successfully. Column headers have been populated." }),
          "success"
        );
      } else {
        showAlertMessage(_get(response, "message", intl.formatMessage({ id: "client_template.manage_template.upload_headers_error", defaultMessage: "Something went wrong while uploading the file." })));
      }
    } catch (error) {
      showAlertMessage(_get(error, "message", intl.formatMessage({ id: "client_template.manage_template.upload_headers_error", defaultMessage: "Something went wrong while uploading the file." })));
    } finally {
      this._decrementLoader();
    }
  };

  _handleSubmit = async () => {
    const { setState, editTemplateId } = this.props;
    const { simpleColumns, name, /* specialColumns, editTemplateData, */ selectedClient } = this.state;

    let simplePayloadFormat = [];
    let latestSpecialPayload = [];
    let latestSimplePayload = [];
    let finalPayload = [];

    simplePayloadFormat = _reject(_cloneDeep(simpleColumns || []), _isEmpty);

    const validForm = this._validateForm();

    if (!validForm) { return false; }

    if ((simplePayloadFormat || []).length > 0) {

      latestSimplePayload = (simplePayloadFormat || []).map((e, i) => {
        let mode = _get(e, "mode", 2); // integer: 2=System, 3=Constant, 4=Composed
        let fieldLookupId = null;
        let constantValue = null;
        let composedConfig = null;

        const tempFieldLookupId = (_get(e, "columnName.id", null) !== null)
          ? _get(e, "columnName.id", null)
          : (_get(e, "columnName[0].id", null));

        if (tempFieldLookupId === -1 && (mode === 2 || mode === 1)) {
          mode = 1;
        }

        if (mode === 1) {
          // Source/Ignore — single integer field ID (id = -1 for Ignore)
          fieldLookupId = (_get(e, "columnName.id", null) !== null)
            ? _get(e, "columnName.id", null)
            : (_get(e, "columnName[0].id", null));
        } else if (mode === 2) {
          // System — single integer field ID
          fieldLookupId = (_get(e, "columnName.id", null) !== null)
            ? _get(e, "columnName.id", null)
            : (_get(e, "columnName[0].id", null));
        } else if (mode === 4) {
          // Constant — fixed literal value
          constantValue = _get(e, "constantValue", "");
        } else if (mode === 5) {
          // Composed — per-segment pair_separator and key_value_separator
          const cfg = _get(e, "composedConfig", {}) || {};
          const segments = _get(cfg, "segments", []) || [];
          composedConfig = {
            "segments": (segments || []).map((seg, segIdx) => {
              let sourceObj = { "client_template_field_lookup_id": null, "sequence_number": null };
              const compVal = _get(seg, "composedValue", null);
              if (compVal && typeof compVal === 'string') {
                if (compVal.startsWith("col_")) {
                  sourceObj.sequence_number = parseInt(compVal.split("col_")[1], 10);
                } else if (compVal.startsWith("fld_")) {
                  sourceObj.client_template_field_lookup_id = parseInt(compVal.split("fld_")[1], 10);
                }
              }
              return {
                "sequence": segIdx + 1,
                "key": _get(seg, "key", ""),
                "pair_separator": _get(seg, "pairSeparator", ""),
                "key_value_separator": _get(seg, "keyValueSeparator", ""),
                "source": sourceObj
              };
            })
          };
        }

        return {
          "client_template_lead_field_lookup_id": fieldLookupId,
          "sequence_number": i,
          "friendly_name": _get(e, "friendlyName", ""),
          "mode": mode,
          "constant_field_value": null,
          "constant_value": constantValue,
          "composed_config": composedConfig
        };
      });
    }

    finalPayload = _sortBy((latestSimplePayload || []).concat(latestSpecialPayload || []), ["sequence_number"]);

    let isSuccess = false;

    try {
      this._incrementLoader();

      const payload = {
        client_id: (_get(selectedClient, "[0].id", null) === null ? _get(selectedClient, "id", "") : _get(selectedClient, "[0].id", null)),
        template_name: (name || ""),
        template_id: (_get(this.props, "isCloneMode", false) ? null : (editTemplateId || null)),
        json: finalPayload
      };
      // console.log("Payload: ", payload);
      const response = await createOrUpdateTemplate(payload);
      if (_get(response, "flag", false) === true) {
        showAlertMessage(_get(response, "data.responseMessage", ""), "success");

        this.setState({ ...this.defaultNoValue, formError: {} });

        isSuccess = true;
      } else {
        showAlertMessage((_get(response, "data.responseMessage", "Something went wrong while saving client template.")));
      }
    } catch (error) {
      showAlertMessage(_get(error, "data.responseMessage", "Something went wrong while saving client template."));

    } finally {
      const { updatePageLevelLoadingState } = this.props;

      // Call loader hide synchronously BEFORE the modal may close (and unmount this component).
      // _decrementLoader relies on this.setState callback which is async/batched and may not
      // fire if the component is unmounted first by the parent setState below.
      if (typeof updatePageLevelLoadingState === "function") {
        updatePageLevelLoadingState(false);
      }
      this.setState((prevState) => ({
        activeRequestCount: Math.max(0, _get(prevState, "activeRequestCount", 0) - 1)
      }));

      if (isSuccess && typeof setState === "function") {
        setState({
          showAddOrUpdateModal: false,
          selectedTemplateId: null,
          isCloneMode: false,
          refreshTable: true
        });
      }
    }
  }

  _validateForm = () => {
    const { intl } = this.props;
    const { name, selectedClient, selectedAgency, simpleColumns, specialColumns } = this.state;

    let manageTemplateColumn = _merge([], _cloneDeep(simpleColumns || []), _cloneDeep(specialColumns || []));

    let error = {};
    let allColumn = [];
    let duplicateValues = [];

    Object.values(simpleColumns || []).forEach(col => {
      const mode = _get(col, "mode", 2); // integer: 2=System, 3=Constant, 4=Composed
      const lowerFriendlyName = _get(col, "friendlyName", "").toLowerCase().trim();
      const displayOrder = _get(col, "displayOrder", 0);

      // Friendly name is required for all modes
      if (_isEmpty(lowerFriendlyName)) {
        error[`friendlyName-${displayOrder}`] = intl.formatMessage(
          { id: "error.required", defaultMessage: "please enter {field}." },
          { field: intl.formatMessage({ id: "client_template.manage_template.friendly_name", defaultMessage: "friendly name" }) }
        );
      }

      if (mode === 1) {
        // Source/Ignore — no additional validation required
      } else if (mode === 2) {
        // System — Hermes field must be selected (and not null)
        const colId = _get(col, "columnName.id", null);
        if (col.columnName === null || colId === null) {
          error[`columnName-${displayOrder}`] = intl.formatMessage(
            { id: "error.select", defaultMessage: "Please select {field}." },
            { field: intl.formatMessage({ id: "client_template.manage_template.column", defaultMessage: "column" }) }
          );
        }
      } else if (mode === 4) {
        // Constant — user value is required
        if (_isEmpty((_get(col, "constantValue", "") || "").trim())) {
          error[`columnName-${displayOrder}`] = intl.formatMessage(
            { id: "error.required", defaultMessage: "please enter {field}." },
            { field: intl.formatMessage({ id: "client_template.manage_template.hermes_field", defaultMessage: "hermes fields" }) }
          );
        }
      } else if (mode === 5) {
        // Composed — at least one segment must have a field selected
        const segments = _get(col, "composedConfig.segments", []) || [];
        const hasSelection = (segments || []).some(seg => seg.composedValue !== null && seg.composedValue !== undefined);
        if (!hasSelection) {
          error[`columnName-${displayOrder}`] = intl.formatMessage(
            { id: "error.select", defaultMessage: "Please select {field}." },
            { field: intl.formatMessage({ id: "client_template.manage_template.column", defaultMessage: "column" }) }
          );
        }
      }

      if (!_isEmpty(lowerFriendlyName)) {
        if ((allColumn || []).includes(lowerFriendlyName) && !(duplicateValues || []).includes(lowerFriendlyName)) {
          duplicateValues.push(lowerFriendlyName);
        }
        if (!(allColumn || []).includes(lowerFriendlyName)) {
          (allColumn || []).push(lowerFriendlyName);
        }
      };
    });

    // Object.values(specialColumns || []).forEach(col => {
    //   const lowerSpecialFriendlyName = _get(col, "friendlyName", "").toLowerCase().trim();

    //   if (_isEmpty(lowerSpecialFriendlyName)) {
    //     error[`specialFriendly-${(col.displayOrder || 0)}`] = intl.formatMessage({ id: "error.required", defaultMessage: "please enter {field}." },
    //       { field: intl.formatMessage({ id: "client_template.manage_template.friendly_name", defaultMessage: "friendly name" }) })
    //   }

    //   if (!_isEmpty(lowerSpecialFriendlyName)) {
    //     if ((allColumn || []).includes(lowerSpecialFriendlyName) && !(duplicateValues || []).includes(lowerSpecialFriendlyName)) {
    //       duplicateValues.push(lowerSpecialFriendlyName);
    //     }
    //     if (!(allColumn || []).includes(lowerSpecialFriendlyName)) {
    //       (allColumn || []).push(lowerSpecialFriendlyName);
    //     }
    //   };
    // });

    if (_isEmpty(name)) {
      error["name"] = intl.formatMessage({ id: "error.required", defaultMessage: "please enter {field}." }, {
        field: intl.formatMessage({ id: "client_template.manage_template.input_name", defaultMessage: "name" })
      });
    }
    if (_isEmpty(selectedClient)) {
      error["selectedClient"] = intl.formatMessage({ id: "error.required", defaultMessage: "please enter {field}." }, {
        field: intl.formatMessage({ id: "client_template.manage_template.client", defaultMessage: "client" })
      });
    }
    if (_isEmpty(selectedAgency)) {
      error["selectedAgency"] = intl.formatMessage({ id: "error.required", defaultMessage: "please enter {field}." }, {
        field: intl.formatMessage({ id: "client_template.manage_template.agency", defaultMessage: "agency" })
      });
    }
    if ((manageTemplateColumn || []).length === 0) {
      error["template"] = showAlertMessage(intl.formatMessage({ id: "error.select_atleast_one", defaultMessage: "Please select at least one {field}." },
        { field: intl.formatMessage({ id: "client_template.manage_template.hermes_field", defaultMessage: "hermes fields" }) }
      ));
    }

    // if duplicate values exist show error message
    if ((duplicateValues || []).length > 0) {
      error["friendlyName"] = showAlertMessage(intl.formatMessage({ id: "client_template.manage_template.friendly_name_can_not_be_the_same_for_many_fields", defaultMessage: "Friendly name can not be the same for many fields." }));
    }

    this.setState({ formError: error });

    return (Object.keys(error).length > 0) ? false : true;
  };

  _handleUpdateFormState = (e) => {

    this.setState({ name: (e.currentTarget.value || "") });
  };

  //TextBox parameter value add on change event
  _setInputData = (e, isSpecialFlag, displayNumber) => {
    const { specialColumns, simpleColumns } = this.state;

    let indexInput = null;
    let tmpSpecialColmns = [];
    let tmpSimpleColmns = [];

    const name = _get(e, "currentTarget.name", "");

    if (isSpecialFlag === true) {

      tmpSpecialColmns = _cloneDeep(specialColumns || []);
      tmpSpecialColmns = (typeof tmpSpecialColmns === "object") ? Object.values(tmpSpecialColmns || []) : tmpSpecialColmns;
      tmpSpecialColmns = _reject((tmpSpecialColmns || []), _isEmpty);

      indexInput = _findIndex((tmpSpecialColmns || []), { "displayOrder": displayNumber });

      tmpSpecialColmns = _set((tmpSpecialColmns || []), `[${indexInput}].${name}`, _get(e, "currentTarget.value", ""));

      this.setState({ specialColumns: (tmpSpecialColmns || []) });

    } else if (isSpecialFlag === false) {

      tmpSimpleColmns = _cloneDeep(simpleColumns || []);
      tmpSimpleColmns = (typeof tmpSimpleColmns === "object") ? Object.values(tmpSimpleColmns || []) : tmpSimpleColmns;
      tmpSimpleColmns = _reject((tmpSimpleColmns || []), _isEmpty);

      indexInput = _findIndex((tmpSimpleColmns || []), { "displayOrder": displayNumber });

      tmpSimpleColmns = _set((tmpSimpleColmns || []), `[${indexInput}].${name}`, _get(e, "currentTarget.value", ""));

      this.setState({ simpleColumns: (tmpSimpleColmns || []) });
    }
  };

  //special column Inner TextBox parameter value add
  // _setInnerInputData = (e, displayNumber, index) => {
  //   const { simpleColumns, specialColumns } = this.state;

  //   let tmpSimpleColumns = [];
  //   let tmpSpecialColumns = [];
  //   let specialTemplate = [];
  //   let tmpColmns = [];
  //   let displayIndex = null;

  //   tmpSimpleColumns = _cloneDeep(simpleColumns || []);
  //   tmpSpecialColumns = _cloneDeep(specialColumns || []);

  //   tmpColmns = _sortBy(Object.values(tmpSimpleColumns || []).concat((Object.values(tmpSpecialColumns || []))), ["displayOrder"]);
  //   tmpColmns = _reject((tmpColmns || []), _isEmpty);

  //   displayIndex = _findIndex((tmpColmns || []), { "displayOrder": displayNumber });

  //   tmpColmns[displayIndex].columnFields[index].concatText = _get(e, "currentTarget.value", "");

  //   specialTemplate.push(..._filter((tmpColmns || []), (v) => (_isEqual(v.isSpecial, true))));

  //   this.setState({ specialColumns: (specialTemplate || []) });
  // };

  //Select parameter value add on change event
  _setDropdownValue = (selectedOptions, name, isSpecialFlag, displayNumber) => {
    const { specialColumns, simpleColumns } = this.state;

    let indexDropDown = null;
    let tmpSpecialColmns = [];
    let tmpSimpleColmns = [];

    if (isSpecialFlag === true) {

      tmpSpecialColmns = _cloneDeep(specialColumns || []);
      tmpSpecialColmns = (typeof tmpSpecialColmns === "object") ? Object.values(tmpSpecialColmns || []) : tmpSpecialColmns;
      tmpSpecialColmns = _reject((tmpSpecialColmns || []), _isEmpty);

      indexDropDown = _findIndex((tmpSpecialColmns || []), { "displayOrder": displayNumber });

      tmpSpecialColmns = _set((tmpSpecialColmns || []), `[${indexDropDown}].${name}`, (selectedOptions || null));

      this.setState({ specialColumns: (tmpSpecialColmns || []) });
    } else if (isSpecialFlag === false) {

      tmpSimpleColmns = _cloneDeep(simpleColumns || []);
      tmpSimpleColmns = (typeof tmpSimpleColmns === "object") ? Object.values(tmpSimpleColmns || []) : tmpSimpleColmns;
      tmpSimpleColmns = _reject((tmpSimpleColmns || []), _isEmpty);

      indexDropDown = _findIndex((tmpSimpleColmns || []), { "displayOrder": displayNumber });

      tmpSimpleColmns = _set((tmpSimpleColmns || []), `[${indexDropDown}].${name}`, (selectedOptions || null));

      // If the selected hermes field is Ignore (-1), set mode to 1 (Source/Ignore);
      // otherwise restore to mode 2 (System) for system-mode rows.
      if (name === "columnName") {
        const selectedId = _get(selectedOptions, "id", null);
        const currentMode = _get(tmpSimpleColmns, `[${indexDropDown}].mode`, 2);
        if (currentMode !== 4 && currentMode !== 3) {
          tmpSimpleColmns = _set(
            tmpSimpleColmns,
            `[${indexDropDown}].mode`,
            selectedId === -1 ? 1 : 2
          );
        }
      }

      this.setState({ simpleColumns: (tmpSimpleColmns || []) });
    }
  };

  //special column Inner DropDown parameter value add
  // _setInnerDropdownValue = (selectedOptions, displayValue, index) => {
  //   const { simpleColumns, specialColumns } = this.state;

  //   let tmpSimpleColumns = [];
  //   let tmpSpecialColumns = [];
  //   let specialTemplate = [];
  //   let tmpColmns = [];
  //   let displayIndex = null;

  //   tmpSimpleColumns = _cloneDeep(simpleColumns || []);
  //   tmpSpecialColumns = _cloneDeep(specialColumns || []);

  //   tmpColmns = _sortBy(Object.values(tmpSimpleColumns || []).concat((Object.values(tmpSpecialColumns || []))), ["displayOrder"]);
  //   tmpColmns = _reject((tmpColmns || []), _isEmpty);

  //   displayIndex = _findIndex((tmpColmns || []), { "displayOrder": displayValue });

  //   tmpColmns[displayIndex].columnFields[index].columnName = selectedOptions;

  //   const filterColumns = _filter((tmpColmns || []), (v) => (_isEqual(v.isSpecial, true)));
  //   specialTemplate.push(...filterColumns);

  //   this.setState({ specialColumns: (specialTemplate || []) });
  // };

  _removeTemplateColumn(isSpecialFlag, displayNumber) {
    const { specialColumns, simpleColumns } = this.state;

    let index = null;
    let tmpSpecialColmns = [];
    let tmpSimpleColmns = [];

    if (isSpecialFlag === true) {

      tmpSpecialColmns = _cloneDeep(specialColumns || []);

      tmpSpecialColmns = (typeof tmpSpecialColmns === "object") ? Object.values(tmpSpecialColmns || []) : tmpSpecialColmns;
      tmpSpecialColmns = _reject((tmpSpecialColmns || []), _isEmpty);

      index = _findIndex((tmpSpecialColmns || []), { "displayOrder": displayNumber });

      delete tmpSpecialColmns[index];

      this.setState({ specialColumns: (tmpSpecialColmns || []) });
    } else if (isSpecialFlag === false) {

      tmpSimpleColmns = _cloneDeep(simpleColumns || []);

      tmpSimpleColmns = (typeof tmpSimpleColmns === "object") ? Object.values(tmpSimpleColmns || []) : tmpSimpleColmns;
      tmpSimpleColmns = _reject((tmpSimpleColmns || []), _isEmpty);

      index = _findIndex((tmpSimpleColmns || []), { "displayOrder": displayNumber });

      delete tmpSimpleColmns[index];

      this.setState({ simpleColumns: tmpSimpleColmns });
    }
  };

  _handleSimpleTemplate() {
    this._handleAddRow(2); // 2 = System mode
  };

  _handleAddRow(mode) {
    // mode: 2=System, 4=Constant, 5=Composed
    const { simpleColumns, specialColumns, indexNo } = this.state;

    let tmpSimpleColumns = [];
    let tmpSpecialColumns = [];
    let tmpColmns = [];

    let defaultCol;
    if (mode === 5) {
      defaultCol = this.defaultComposedColumn;
    } else if (mode === 4) {
      defaultCol = this.defaultConstantColumn;
    } else {
      defaultCol = this.defaultSystemColumn;
    }

    tmpSimpleColumns = _cloneDeep(simpleColumns || []);
    tmpSpecialColumns = _cloneDeep(specialColumns || []);

    tmpColmns = _sortBy(Object.values(tmpSimpleColumns || []).concat((Object.values(tmpSpecialColumns || []))), ["displayOrder"]);
    tmpColmns = _reject((tmpColmns || []), _isEmpty);

    let maxDisplayOrder = -1;
    if (tmpColmns.length > 0) {
      maxDisplayOrder = tmpColmns[tmpColmns.length - 1].displayOrder;
    }
    const nextDisplayOrder = Math.max(indexNo, maxDisplayOrder + 1);

    this.setState(prevState => ({
      simpleColumns: {
        ...prevState.simpleColumns,
        [indexNo]: {
          ...defaultCol,
          displayOrder: nextDisplayOrder
        },
      },
      indexNo: Math.max(indexNo, nextDisplayOrder) + 1
    }));
  };

  // --- Composed row sub-field helpers ---

  _addComposedField = (displayOrder) => {
    const { simpleColumns } = this.state;
    const tmpCols = _reject(_cloneDeep(Object.values(simpleColumns || [])), _isEmpty);
    const rowIdx = _findIndex(tmpCols, { "displayOrder": displayOrder });
    if (rowIdx !== -1) {
      if (!Array.isArray(_get(tmpCols[rowIdx], "composedConfig.segments"))) {
        tmpCols[rowIdx].composedConfig = { segments: [] };
      }
      tmpCols[rowIdx].composedConfig.segments.push({ composedValue: null, key: "", pairSeparator: "", keyValueSeparator: "" });
    }
    this.setState({ simpleColumns: tmpCols });
  };

  _removeComposedField = (displayOrder, fieldIndex) => {
    const { simpleColumns } = this.state;
    const tmpCols = _reject(_cloneDeep(Object.values(simpleColumns || [])), _isEmpty);
    const rowIdx = _findIndex(tmpCols, { "displayOrder": displayOrder });
    if (rowIdx !== -1 && Array.isArray(_get(tmpCols[rowIdx], "composedConfig.segments"))) {
      tmpCols[rowIdx].composedConfig.segments.splice(fieldIndex, 1);
      // Always keep at least one entry
      if (tmpCols[rowIdx].composedConfig.segments.length === 0) {
        tmpCols[rowIdx].composedConfig.segments = [{ composedValue: null, key: "", pairSeparator: "", keyValueSeparator: "" }];
      }
    }
    this.setState({ simpleColumns: tmpCols });
  };

  _setComposedDropdownValue = (opt, displayOrder, fieldIndex) => {
    const { simpleColumns } = this.state;
    const tmpCols = _reject(_cloneDeep(Object.values(simpleColumns || [])), _isEmpty);
    const rowIdx = _findIndex(tmpCols, { "displayOrder": displayOrder });
    if (rowIdx !== -1 && Array.isArray(_get(tmpCols[rowIdx], "composedConfig.segments"))) {
      tmpCols[rowIdx].composedConfig.segments[fieldIndex].composedValue = _get(opt, "id", null);
    }
    this.setState({ simpleColumns: tmpCols });
  };

  _setComposedKey = (value, displayOrder, fieldIndex) => {
    const { simpleColumns } = this.state;
    const tmpCols = _reject(_cloneDeep(Object.values(simpleColumns || [])), _isEmpty);
    const rowIdx = _findIndex(tmpCols, { "displayOrder": displayOrder });
    if (rowIdx !== -1 && Array.isArray(_get(tmpCols[rowIdx], "composedConfig.segments"))) {
      tmpCols[rowIdx].composedConfig.segments[fieldIndex].key = value;
    }
    this.setState({ simpleColumns: tmpCols });
  };

  _setComposedKeyValueSeparator = (value, displayOrder, fieldIndex) => {
    const { simpleColumns } = this.state;
    const tmpCols = _reject(_cloneDeep(Object.values(simpleColumns || [])), _isEmpty);
    const rowIdx = _findIndex(tmpCols, { "displayOrder": displayOrder });
    if (rowIdx !== -1 && Array.isArray(_get(tmpCols[rowIdx], "composedConfig.segments"))) {
      tmpCols[rowIdx].composedConfig.segments[fieldIndex].keyValueSeparator = value;
    }
    this.setState({ simpleColumns: tmpCols });
  };

  _setComposedPairSeparator = (value, displayOrder, fieldIndex) => {
    const { simpleColumns } = this.state;
    const tmpCols = _reject(_cloneDeep(Object.values(simpleColumns || [])), _isEmpty);
    const rowIdx = _findIndex(tmpCols, { "displayOrder": displayOrder });
    if (rowIdx !== -1 && Array.isArray(_get(tmpCols[rowIdx], "composedConfig.segments"))) {
      tmpCols[rowIdx].composedConfig.segments[fieldIndex].pairSeparator = value;
    }
    this.setState({ simpleColumns: tmpCols });
  };

  // _handleSpecialTemplate() {
  //   const { simpleColumns, specialColumns, indexNo } = this.state;

  //   let tmpSimpleColumns = [];
  //   let tmpSpecialColumns = [];
  //   let tmpSpColmns = [];
  //   let lastColumn = [];
  //   let currentIndex = null;

  //   tmpSimpleColumns = _cloneDeep(simpleColumns || []);
  //   tmpSpecialColumns = _cloneDeep(specialColumns || []);

  //   tmpSpColmns = _sortBy(Object.values(tmpSimpleColumns || []).concat((Object.values(tmpSpecialColumns || []))), ["displayOrder"]);

  //   tmpSpColmns = _reject((tmpSpColmns || []), _isEmpty);

  //   lastColumn = _findLast((tmpSpColmns || []), (n) => {
  //     return n.displayOrder > indexNo
  //   });

  //   if (indexNo < (lastColumn || []).displayOrder) {

  //     currentIndex = (lastColumn || []).displayOrder + 1;

  //     this.setState(prevState => ({
  //       specialColumns: {
  //         ...prevState.specialColumns,
  //         [indexNo]: {
  //           ...this.defaultSpecialColumn,
  //           displayOrder: currentIndex
  //         },
  //       },
  //       indexNo: currentIndex + 1
  //     }))

  //   } else {

  //     this.setState(prevState => ({
  //       specialColumns: {
  //         ...prevState.specialColumns,
  //         [indexNo]: {
  //           ...this.defaultSpecialColumn,
  //           displayOrder: indexNo
  //         },
  //       },
  //       indexNo: indexNo + 1
  //     }))
  //   }
  // };

  _addNewSpecialInnerRow(index) {
    const { specialColumns } = this.state;

    let tmpSpecialColmns = [];
    tmpSpecialColmns = _reject(_cloneDeep(specialColumns || []), _isEmpty);

    Object.values(tmpSpecialColmns || []).forEach(column => {

      if (column.displayOrder === index) {
        column.columnFields.push(this.defaultInnerColumnFields);
      }
    });

    this.setState({ specialColumns: _cloneDeep(tmpSpecialColmns || []) });
  };

  _minusSpecialInnerRow(index) {
    const { specialColumns } = this.state;

    let tmpSpecialColmns = [];
    tmpSpecialColmns = _reject(_cloneDeep(specialColumns || []), _isEmpty);

    Object.values(tmpSpecialColmns || []).forEach(column => {

      if ((column.displayOrder === index)) {
        column.columnFields.pop();
      }
    });

    this.setState({ specialColumns: _cloneDeep(tmpSpecialColmns || []) });
  };

  _reOrderColumns = (list, fromDisplayOrder, toDisplayOrder) => {

    let resetList = _cloneDeep(list || []);

    const [removed] = resetList.splice(fromDisplayOrder, 1);
    resetList.splice(toDisplayOrder, 0, removed);

    resetList = _reject((resetList || []), _isEmpty);

    Object.values(resetList || []).forEach((e, i) => {
      e.displayOrder = i;
    });

    return resetList;
  };

  onDragEnd = (result) => {
    const { simpleColumns, specialColumns } = this.state;

    let tmpSimpleColumns = [];
    let tmpSpecialColumns = [];
    let tmpSpColmns = [];
    let specialTemplate = [];
    let simpleTemplate = [];
    let reOrderTemplate = [];

    tmpSimpleColumns = _cloneDeep(simpleColumns || []);
    tmpSpecialColumns = _cloneDeep(specialColumns || []);

    tmpSpColmns = _sortBy(Object.values(tmpSimpleColumns || []).concat((Object.values(tmpSpecialColumns || []))), ["displayOrder"]);
    tmpSpColmns = _reject((tmpSpColmns || []), _isEmpty);

    // dropped outside the list
    if (result.destination === null) {
      return;
    }

    reOrderTemplate = this._reOrderColumns(
      tmpSpColmns,
      result.source.index,
      result.destination.index
    );

    let reArrangeSpecialTemplate = _filter((reOrderTemplate || []), (v) => (_isEqual(v.isSpecial, true)));
    specialTemplate.push(...reArrangeSpecialTemplate);

    let reArrangeSimpleTemplate = _filter((reOrderTemplate || []), (v) => (_isEqual(v.isSpecial, false)));
    simpleTemplate.push(...reArrangeSimpleTemplate);

    this.setState({ specialColumns: (specialTemplate || []), simpleColumns: (simpleTemplate || []) });
  };

  //Click on Increment button then column fields add dynamically
  // _renderInnerColumnFields(index, key) {
  //   const { masterColumnsList } = this.state;
  //   let tmpInnerColumns = [];

  //   tmpInnerColumns = _get(key, "columnFields", []);

  //   return (
  //     <>
  //       {Object.values(tmpInnerColumns || []).map((i, e) => {

  //         return (
  //           <div key={`inner-${e}`} className="row">
  //             <div key={`inner-select-${e}`} className="col-xs-4 col-sm-4 col-md-4 m-l-30 mt-1">
  //               <div className="form-group">
  //                 <Select
  //                   className="text-capitalize"
  //                   value={(i.columnName || [])}
  //                   options={(masterColumnsList || [])}
  //                   getOptionValue={(option) => (option.id)}
  //                   getOptionLabel={(option) => (option.friendly_name)}
  //                   isMulti={false}
  //                   onChange={(selectedOptions) => this._setInnerDropdownValue(selectedOptions, index, e)}
  //                 />
  //               </div>
  //             </div>
  //             <div key={`inner-text-${e}`} className="col-xs-2 col-sm-2 col-md-2 input text mt-1">
  //               <input
  //                 type="text"
  //                 name="concatText"
  //                 className="form-control"
  //                 value={(i.concatText || "")}
  //                 onChange={(k) => this._setInnerInputData(k, index, e)}
  //               />
  //             </div>
  //           </div>
  //         )
  //       })}
  //     </>
  //   )
  // }

  //Click on add button dynamically add div template
  _renderSimpleColumns(index, key, dragHandleProps) {
    const { masterColumnsList, compositeColumnsList, formError } = this.state;
    const { intl } = this.props;

    const mode = _get(key, "mode", 2); // integer: 2=System, 3=Constant, 4=Composed

    const compositeFieldOptions = (compositeColumnsList || []);

    // --- Hermes Field cell: differs per mode ---
    let hermesFieldCell;

    if (mode === 4) {
      // Constant — plain text input, user types a fixed literal value
      hermesFieldCell = (
        <div className="form-group">
          <input
            type="text"
            name="constantValue"
            className="form-control"
            value={(_get(key, "constantValue", "") || "")}
            onChange={(k) => this._setInputData(k, key.isSpecial, key.displayOrder)}
            placeholder={intl.formatMessage({ id: "client_template.manage_template.constant_value", defaultMessage: "Enter constant value" })}
          />
          {(_get(formError, `columnName-${index}`, "") !== "") && (<ErrorMessage message={_get(formError, `columnName-${index}`, "")} />)}
        </div>
      );
    } else if (mode === 5) {
      // Composed — multiple sub-rows: [Key] [KV Sep.] [Select Column] [Pair Sep.] [+/-]
      // Each segment has its own independent pair_separator, key_value_separator, and key.
      const composedConfig = _get(key, "composedConfig", { segments: [{ composedValue: null, key: "", pairSeparator: "", keyValueSeparator: "" }] });
      const segments = _get(composedConfig, "segments", [{ composedValue: null, key: "", pairSeparator: "", keyValueSeparator: "" }]);

      hermesFieldCell = (
        <div>
          {(segments || []).map((field, fieldIdx) => {
            const isLast = fieldIdx === segments.length - 1;
            const selectedOption = (compositeFieldOptions || []).find(c => c.id === field.composedValue) || null;
            return (
              <div key={`composed-${key.displayOrder}-${fieldIdx}`} className="d-flex align-items-center mb-1" style={{ gap: "6px" }}>
                <div style={{ flex: "1 1 0", minWidth: 0 }}>
                  <input
                    type="text"
                    className="form-control"
                    value={(field.key || "")}
                    onChange={(e) => this._setComposedKey(e.target.value, key.displayOrder, fieldIdx)}
                    placeholder={intl.formatMessage({ id: "client_template.manage_template.key", defaultMessage: "Key" })}
                  />
                </div>
                <div style={{ width: "80px", flexShrink: 0 }}>
                  <input
                    type="text"
                    className="form-control"
                    value={(field.keyValueSeparator || "")}
                    onChange={(e) => this._setComposedKeyValueSeparator(e.target.value, key.displayOrder, fieldIdx)}
                    placeholder={intl.formatMessage({ id: "client_template.manage_template.key_value_separator", defaultMessage: "KV Sep." })}
                  />
                </div>
                <div style={{ flex: "1 1 0", minWidth: 0 }}>
                  <Select
                    className="text-capitalize"
                    placeholder={intl.formatMessage({ id: "client_template.manage_template.select_column", defaultMessage: "Select Column" })}
                    value={selectedOption}
                    options={compositeFieldOptions}
                    getOptionValue={(option) => option.id}
                    getOptionLabel={(option) => option.friendly_name}
                    onChange={(opt) => this._setComposedDropdownValue(opt, key.displayOrder, fieldIdx)}
                    isMulti={false}
                  />
                </div>
                <div style={{ width: "80px", flexShrink: 0 }}>
                  <input
                    type="text"
                    className="form-control"
                    value={(field.pairSeparator || "")}
                    onChange={(e) => this._setComposedPairSeparator(e.target.value, key.displayOrder, fieldIdx)}
                    placeholder={intl.formatMessage({ id: "client_template.manage_template.pair_separator", defaultMessage: "Pair Sep." })}
                  />
                </div>
                <button
                  type="button"
                  className={`btn btn-sm ${isLast ? "btn-outline-primary" : "btn-outline-danger"} px-2`}
                  style={{ flexShrink: 0, minWidth: "30px" }}
                  onClick={() => isLast
                    ? this._addComposedField(key.displayOrder)
                    : this._removeComposedField(key.displayOrder, fieldIdx)
                  }
                >
                  {isLast ? "+" : "−"}
                </button>
              </div>
            );
          })}
          {(_get(formError, `columnName-${index}`, "") !== "") && (<ErrorMessage message={_get(formError, `columnName-${index}`, "")} />)}
        </div>
      );
    } else {
      // System (mode 2) — single-select Hermes field dropdown (includes Ignore option)
      hermesFieldCell = (
        <div className="form-group">
          <Select
            className="text-capitalize"
            placeholder={intl.formatMessage({ id: "client_template.manage_template.select_column", defaultMessage: "select column" })}
            value={(key.columnName || null)}
            options={(masterColumnsList || [])}
            getOptionValue={(option) => (option.id)}
            getOptionLabel={(option) => (option.friendly_name)}
            onChange={(selectedOptions) => this._setDropdownValue(selectedOptions, "columnName", key.isSpecial, key.displayOrder)}
            isMulti={false}
          />
          {(_get(formError, `columnName-${index}`, "") !== "") && (<ErrorMessage message={_get(formError, `columnName-${index}`, "")} />)}
        </div>
      );
    }

    return (
      <div key={`simple-${index}`} className="my-2">
        <div className="row align-items-start">
          <div {...(dragHandleProps || {})} className="input text col-xs-1 col-sm-1 col-md-1 d-flex flex-column justify-content-center align-items-center cursor-pointer">
            <div>
              <img src={require("../../../../assets/icons/icon-chev-up-primary.svg").default} className="img-fluid" alt="up" />
            </div>
            <div>
              <img src={require("../../../../assets/icons/icon-chev-down-primary.svg").default} className="img-fluid" alt="down" />
            </div>
          </div>
          <div className="input text col-xs-4 col-sm-4 col-md-4">
            <input
              type="text"
              name="friendlyName"
              className="form-control"
              value={(key.friendlyName || "")}
              onChange={(k) => this._setInputData(k, key.isSpecial, key.displayOrder)}
            />
            {(_get(formError, `friendlyName-${index}`, "") !== "") && (<ErrorMessage message={_get(formError, `friendlyName-${index}`, "")} />)}
          </div>
          <div
            className={`col-xs-6 col-sm-6 ${mode === 5 ? "col-md-9 p-4 bg-grey-ed" : "col-md-6"}`}
            style={mode === 5 ? { marginLeft: "93px" } : {}}
          >
            {hermesFieldCell}
          </div>
          <div className="col-xs-1 col-sm-1 col-md-1 mt-2" onClick={() => this._removeTemplateColumn(key.isSpecial, key.displayOrder)}>
            <img src={require("../../../../assets/icons/icon-close.svg").default} className="img-fluid" alt="img" />
          </div>
        </div>
      </div>
    );
  };

  //click on add Special button Dynamically div template add
  // _renderSpecialColumns(index, key) {
  //   const { formError } = this.state;

  //   return (
  //     <div key={`div-${index}`} className="my-2">
  //       <div className="row">
  // <img src={require("../../../../assets/icons/icon-chev-up-primary.svg").default} className="img-fluid arrow-up" alt="up" />
  // <img src={require("../../../../assets/icons/icon-chev-down-primary.svg").default} className="img-fluid arrow-down" alt="down" />
  //         <div className="col-xs-7 col-sm-7 col-md-7">
  //           <div className="form-group">
  //             <Select
  //               className="text-capitalize"
  //               value={(key.concatType || null)}
  //               options={(concatenateTypeList || [])}
  //               getOptionValue={(e) => (e.id || 0)}
  //               getOptionLabel={(e) => (e.name || "")}
  //               onChange={(selectedOptions) => this._setDropdownValue(selectedOptions, "concatType", key.isSpecial, key.displayOrder)}
  //               isMulti={false}
  //             />
  //           </div>
  //         </div>
  //         <div className="input text col-xs-3 col-sm-3 col-md-3">
  //           <input
  //             type="text"
  //             name="friendlyName"
  //             className="form-control"
  //             value={(key.friendlyName || "")}
  //             onChange={(k) => this._setInputData(k, key.isSpecial, key.displayOrder)}
  //           />
  //           {(_get(formError, `specialFriendly-${index}`, "") !== "") && (<ErrorMessage message={_get(formError, `specialFriendly-${index}`, "")} />)}
  //         </div>
  //         <div className="col-xs-1 col-sm-1 col-md-1 m-t-8" onClick={() => this._removeTemplateColumn(key.isSpecial, key.displayOrder)}>
  //           <i className="far fa-times-circle fa-2x add-minus-icon" />
  //         </div>
  //       </div>

  //       {this._renderInnerColumnFields(index, key)}

  //       <div className="row">
  //         <div className="col-xs-12 col-sm-12 col-md-12 mt-1">
  //           <span onClick={() => this._addNewSpecialInnerRow(index)}>
  //             <img src={require("../../../../assets/icons/icon-plus.svg").default} className="img-fluid" alt="img" />
  //           </span>
  //           <span className="ms-2" onClick={() => this._minusSpecialInnerRow(index)}>
  //             <img src={require("../../../../assets/icons/icon-minus.svg").default} className="img-fluid" alt="img" />
  //           </span>
  //         </div>
  //       </div>
  //     </div>
  //   )
  // };

  // Show all the field columns
  _renderColumns() {
    const { simpleColumns, specialColumns } = this.state;

    let tmpSimpleColumns = [];
    let tmpSpecialColumns = [];
    let tmpColmns = [];

    tmpSimpleColumns = _cloneDeep(simpleColumns || []);
    tmpSimpleColumns = (typeof tmpSimpleColumns === "object") ? Object.values(tmpSimpleColumns || []) : tmpSimpleColumns;

    tmpSpecialColumns = _cloneDeep(specialColumns || []);
    tmpSpecialColumns = (typeof tmpSpecialColumns === "object") ? Object.values(tmpSpecialColumns || []) : tmpSpecialColumns;

    tmpColmns = _sortBy(Object.values(tmpSimpleColumns || []).concat((Object.values(tmpSpecialColumns || []))), ["displayOrder"]);

    return (
      <DragDropContext onDragEnd={(e) => this.onDragEnd(e)}>
        <Droppable droppableId="droppable">
          {(provided) => (
            <div className="col-xs-12 col-sm-12 col-md-12 my-2"
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {((tmpColmns || []).map((e, i) => {
                if (typeof e === "object") {
                  return (
                    <Draggable key={`item-${e.displayOrder}`} draggableId={`item-${e.displayOrder}`} index={e.displayOrder}>
                      {(provided) => (
                        <div key={`item-drag-${i}`}
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                        >
                          {this._renderSimpleColumns(e.displayOrder, e, provided.dragHandleProps)}
                        </div>
                      )}
                    </Draggable>
                  );
                }
                return null
              }))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    );
  };

  render() {
    const { filterAgencyList, editTemplateId, isCloneMode, allowedTemplateFilesLabel, intl } = this.props;
    const { name, formError, simpleColumns, specialColumns, filterClientList, selectedClient, selectedAgency } = this.state;

    const isAddMode = !editTemplateId && !isCloneMode;

    let combinedTemplate = _sortBy(Object.values(_cloneDeep(simpleColumns || [])).concat((Object.values(_cloneDeep(specialColumns || [])))), ["displayOrder"]);
    combinedTemplate = _reject((combinedTemplate || []), _isEmpty);

    return (
      <div className="row">
        <div className="col-sm-4 col-md-4">
          <div className="form-group">
            <label className="text-capitalize">
              <FormattedMessage id="client_template.manage_template.input_name" defaultMessage="Name" />
            </label>
            <div className="input text">
              <input
                type="text"
                name="name"
                className="form-control"
                value={(name || "")}
                onChange={(e) => this._handleUpdateFormState(e)} />
              {(formError.name !== "") && (<ErrorMessage message={formError.name} />)}
            </div>
          </div>
        </div>
        <div className="col-sm-4 col-md-4">
          <div className="form-group">
            <label className="text-capitalize">
              <FormattedMessage id="client_template.manage_template.agency" defaultMessage="Agency" />
            </label>
            <Select
              className="text-capitalize"
              placeholder={intl.formatMessage({ id: "client_template.manage_template.select_agency", defaultMessage: "Select agency" })}
              value={(selectedAgency || [])}
              options={(filterAgencyList || [])}
              getOptionValue={(option) => (option.id)}
              getOptionLabel={(option) => (option.agency_name)}
              onChange={(y) => this.setState({ selectedAgency: (y) })}
              isMulti={false}
              isDisabled={(editTemplateId && !isCloneMode) ? true : false}
            />
            {(formError.selectedAgency !== "") && (<ErrorMessage message={formError.selectedAgency} />)}
          </div>
        </div>
        <div className="col-sm-4 col-md-4">
          <div className="form-group">
            <label className="text-capitalize">
              <FormattedMessage id="client_template.manage_template.client" defaultMessage="Client" />
            </label>
            <Select
              className="text-capitalize"
              placeholder={intl.formatMessage({ id: "client_template.manage_template.select_client", defaultMessage: "Select Client" })}
              value={(selectedClient || [])}
              options={(filterClientList || [])}
              getOptionValue={(option) => (option.id)}
              getOptionLabel={(option) => (option.client_name)}
              onChange={(y) => this.setState({ selectedClient: (y) })}
              isMulti={false}
              isDisabled={(editTemplateId && !isCloneMode) ? true : false}
            />
            {(formError.selectedClient !== "") && (<ErrorMessage message={formError.selectedClient} />)}
          </div>
        </div>

        {isAddMode && (
          <div className="col-sm-4 col-md-4 mt-4">
            <div className="form-group">
              <label className="text-capitalize">
                <FormattedMessage id="client_template.manage_template.upload_headers_file_label" defaultMessage="Upload Headers File" />
              </label>
              <Dropzone
                maxFiles={1}
                multiple={false}
                accept={{ "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"], "text/csv": [".csv"] }}
                onDrop={(f) => this._onDropTemplateFile(f)}
                ref={templateDropzoneRef}
              >
                {({ getRootProps, getInputProps }) => (
                  <div className="mb-6" {...getRootProps({ className: "dropzone" })}>
                    <div>
                      <div className="dropzone-input form-control">
                        <span>Browse...</span>
                        No file selected.
                      </div>
                      <input {...getInputProps()} />
                    </div>
                    <label htmlFor="singleUpload" className="mt-2 text-grey-93">
                      <FormattedMessage
                        id="file.allowed_files"
                        defaultMessage="Allowed file extensions : {allowedExtensions}"
                        values={{ allowedExtensions: allowedTemplateFilesLabel }}
                      />
                    </label>
                  </div>
                )}
              </Dropzone>
              <small className="text-muted mt-1 d-block">
                <FormattedMessage id="client_template.manage_template.upload_headers_description" defaultMessage="Upload an Excel/CSV file to auto-populate column headers." />
              </small>
            </div>
          </div>
        )}

        {((combinedTemplate || []).length > 0) && (
          <div className="col-sm-12 col-md-12 mt-5">
            <div className="cardBox-header row">
              <div className="col-xs-1 col-sm-1 col-md-1 text-capitalize p-l-32" />
              <div className="col-xs-4 col-sm-4 col-md-4 text-capitalize p-l-32">
                <h5><FormattedMessage id="client_template.manage_template.friendly_name" defaultMessage="Friendly Name" /></h5>
              </div>
              <div className="col-xs-4 col-sm-4 col-md-4 text-capitalize p-l-32">
                <h5><FormattedMessage id="client_template.manage_template.hermes_field" defaultMessage="Hermes Field" /></h5>
              </div>
            </div>
          </div>
        )}

        {this._renderColumns()}
        <div className="col-sm-12 col-md-12 py-5">
          <div className="form-group m-t-15 text-left">
            <Dropdown className="d-inline-block">
              <Dropdown.Toggle as="button" type="button" className="btn btn-dark me-5 dropdown-toggle">
                <FormattedMessage id="client_template.manage_template.btn.add" defaultMessage="Add" />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => this._handleAddRow(2)}>
                  <FormattedMessage id="client_template.manage_template.btn.add_system_row" defaultMessage="Add System Row" />
                </Dropdown.Item>
                <Dropdown.Item onClick={() => this._handleAddRow(4)}>
                  <FormattedMessage id="client_template.manage_template.btn.add_constant_row" defaultMessage="Add Constant Row" />
                </Dropdown.Item>
                <Dropdown.Item onClick={() => this._handleAddRow(5)}>
                  <FormattedMessage id="client_template.manage_template.btn.add_composed_row" defaultMessage="Add Composed Row" />
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
        <div className="col-sm-12 col-md-12">
          <div className="form-group text-end">
            <button type="submit" className="btn btn-primary" onClick={() => this._handleSubmit()}>
              <FormattedMessage id="btn.submit" defaultMessage="Submit" />
            </button>
          </div>
        </div>
      </div>
    )
  }
}

const mapStateToProps = (state, props) => ({
  allowedTemplateFilesLabel: _get(state, "application.constants.allowedTemplateFilesLabel", ""),
});

const mapDispatchToProps = (dispatch) => ({
  updatePageLevelLoadingState: (data) => dispatch(updatePageLevelLoadingState(data)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(AddOrUpdateTemplate)));
